import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import V2Header from "../../components/v2/header";
import ContactUsDrawer from "../../components/v2/contact-us-drawer";
import { RouterConstant } from "../../routers/index";
import { useUserStore } from "../../utils/userStore";
import SideMenu from "../../components/side-menu";
import { StudyModeModal } from "../../components/v2/action-buttons";
import { getRequiredRoute } from "../../utils/navigationUtils";
import { UserService } from "../../services/user/user.service";
import { useGamificationStore } from "../../utils/gamificationStore";
import { useNotificationStore } from "../../utils/notificationStore";
import { contactusIcon } from '../../assets/images';
import "./layout.scss";

interface LayoutContextType {
  t: TFunction;
  toggleMenu: () => void;
  openModesModal: () => void;
  closeModesModal: () => void;
  progressPercentage?: number;
  setProgressPercentage: (val: number | undefined) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayoutContext = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayoutContext must be used within a MainLayout");
  }
  return context;
};

export default function MainLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const currentPath = location.pathname;
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [progressPercentage, setProgressPercentage] = useState<number | undefined>(undefined);
  const [showModesModal, setShowModesModal] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [showFeedbackTooltip, setShowFeedbackTooltip] = useState(false);
  const [hasSynced, setHasSynced] = useState(false);

  const publicRoutes = useMemo(() => [
    RouterConstant.ROUTERS_PATH.ROOT_PATH,
    RouterConstant.ROUTERS_PATH.STARTINGPAGE1,
    RouterConstant.ROUTERS_PATH.LOGIN,
    RouterConstant.ROUTERS_PATH.UPLOADCOMPLETE,
    RouterConstant.ROUTERS_PATH.CAPTUREEXERCISE,
    RouterConstant.ROUTERS_PATH.USERREGISTRATIONINITIAL,
    RouterConstant.ROUTERS_PATH.USERREGISTRATIONSTEP1,
    RouterConstant.ROUTERS_PATH.USERREGISTRATIONOTP,
    RouterConstant.ROUTERS_PATH.RESETPASSWORD,
  ], []);

  const hideHeader = useMemo(() => [
    ...publicRoutes,
    RouterConstant.ROUTERS_PATH.CHOOSEPACKAGES, 
    RouterConstant.ROUTERS_PATH.PAYMENT,
    RouterConstant.ROUTERS_PATH.PAYMENTSTATUS,
    RouterConstant.ROUTERS_PATH.USERPROFILE,
  ].includes(currentPath), [currentPath, publicRoutes]);

  const hideFeedbackTab = useMemo(() => [
    ...publicRoutes,
    RouterConstant.ROUTERS_PATH.CHOOSEPACKAGES, 
    RouterConstant.ROUTERS_PATH.PAYMENT,
    RouterConstant.ROUTERS_PATH.PAYMENTSTATUS,
    RouterConstant.ROUTERS_PATH.USERPROFILE,
    RouterConstant.ROUTERS_PATH.SEARCH_LESSON,
    RouterConstant.ROUTERS_PATH.SEARCHEXRCISE,
  ].includes(currentPath), [currentPath, publicRoutes]);

  const isPublicRoute = useMemo(() => publicRoutes.includes(currentPath), [currentPath, publicRoutes]);

  useEffect(() => {
    if (hideFeedbackTab) return;

    const hasSeenTooltip = sessionStorage.getItem("seen_feedback_tab_tooltip");
    if (!hasSeenTooltip) {
      const timer = setTimeout(() => {
        setShowFeedbackTooltip(true);
        sessionStorage.setItem("seen_feedback_tab_tooltip", "true");

        setTimeout(() => {
          setShowFeedbackTooltip(false);
        }, 3000);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [hideFeedbackTab]);

  const toggleMenu = () => setIsMenuOpen(prev => !prev);
  const closeMenu = () => setIsMenuOpen(false);
  const openModesModal = () => setShowModesModal(true);
  const closeModesModal = () => setShowModesModal(false);

  const handleSelectMode = (id: string) => {
    closeModesModal();
    const modePaths: Record<string, string> = {
      study: RouterConstant.ROUTERS_PATH.SEARCH_LESSON,
      exercise: RouterConstant.ROUTERS_PATH.SEARCHEXRCISE,
      homework: RouterConstant.ROUTERS_PATH.EXERCISES_HOMEWORK,
      exam: RouterConstant.ROUTERS_PATH.SELFTESTSTART,
    };

    const targetPath = modePaths[id];
    if (targetPath) {
      if (window.location.pathname === targetPath) {
        navigate(targetPath, { state: { reset: true, timestamp: Date.now() } });
      } else {
        navigate(targetPath);
      }
    }
  };

  const { 
    userInfo, 
    user_attributes,
    setAttributes, 
    setUserInfo, 
    justLoggedIn, 
    setJustLoggedIn,
    isAuthenticated: getIsAuthenticated
  } = useUserStore();
  
  const isAuthenticated = getIsAuthenticated();

  useEffect(() => {
    // 1. Redirect unauthenticated users from private routes
    if (!isPublicRoute && !isAuthenticated) {
      navigate(RouterConstant.ROUTERS_PATH.ROOT_PATH);
      return;
    }

    // 2. Authenticated users guard rail
    if (isAuthenticated) {
      const requiredRoute = getRequiredRoute(useUserStore.getState());
      
      // If the required route is the final dashboard, it means the user has completed all mandatory steps.
      // In this case, they are free to browse any private route.
      const hasCompletedAllSteps = requiredRoute === RouterConstant.ROUTERS_PATH.USER_MAIN_DASHBOARD;

      // Relaxed constraint: If the user has completed their profile and is only missing onboarding, they can still navigate to other places.
      const isOnlyMissingOnboarding = requiredRoute === RouterConstant.ROUTERS_PATH.V2_ONBOARDING;

      const isAllowedPath = 
        currentPath === requiredRoute || 
        hasCompletedAllSteps || 
        isOnlyMissingOnboarding || 
        currentPath === RouterConstant.ROUTERS_PATH.PAYMENT || 
        currentPath === RouterConstant.ROUTERS_PATH.PAYMENTSTATUS ||
        currentPath === RouterConstant.ROUTERS_PATH.USERPROFILE;

      if (!isAllowedPath && !isPublicRoute) {
        navigate(requiredRoute);
        return;
      }
    }
  }, [currentPath, navigate, isPublicRoute, isAuthenticated, hasSynced, userInfo, user_attributes]);

  // Close menu only on navigation
  useEffect(() => {
    closeMenu();
  }, [currentPath]);

  // Global background sync for authenticated users
  useEffect(() => {
    if (isAuthenticated && !hasSynced) {
      // Priority 3: Skip sync if we just logged in (fresh data already in store)
      if (justLoggedIn) {
        setHasSynced(true);
        setJustLoggedIn(false); // Reset so future refreshes trigger a sync
        
        // Proactively initialize gamification state
        useGamificationStore.getState().initState();
        return;
      }

      const userService = new UserService();
      userService.getCurrentUser()
        .then(async (response: any) => {
          if (response) {
            setUserInfo({
              userId: response.id,
              first_name: response.first_name,
              last_name: response.last_name,
              email: response.email,
              phone: response.phone,
              is_access: response.is_access,
              org_id: response.org_id,
              org_type: response.org_type,
            });
            setAttributes(response.attributes || []);
            setHasSynced(true);
            
            // Proactively initialize gamification state
            useGamificationStore.getState().initState();
          }
        })
        .catch(err => {
          console.error('Global background sync failed:', err);
        });
    }
    
    if (!isAuthenticated && hasSynced) {
      setHasSynced(false);
    }
  }, [isAuthenticated, hasSynced]);

  // Notification unread count polling (every 30 seconds + on route changes)
  useEffect(() => {
    if (!isAuthenticated) return;

    useNotificationStore.getState().fetchUnreadCount();

    const interval = setInterval(() => {
      useNotificationStore.getState().fetchUnreadCount();
    }, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, currentPath]);


  return (
    <LayoutContext.Provider value={{ 
      t, 
      toggleMenu, 
      progressPercentage, 
      setProgressPercentage,
      openModesModal,
      closeModesModal
    }}>
      <div className={`pages-layout ${hideHeader ? 'no-header' : ''}`} dir="rtl">
        {!hideHeader && <V2Header progressPercentage={progressPercentage} />}
        <div className="main-body">
          <Outlet />
        </div>
        <SideMenu isOpen={isMenuOpen} onClose={closeMenu} />
        {showModesModal && (
          <StudyModeModal 
            selectedMode={null} 
            onSelectMode={handleSelectMode} 
            onClose={closeModesModal} 
          />
        )}
        
        {/* App-level Feedback Tab Placeholder */}
        {!hideFeedbackTab && (
          <div className="app-feedback-tab" onClick={() => setIsFeedbackOpen(true)}>
            <img src={contactusIcon} alt="Contact Us" className="feedback-icon" />
            {showFeedbackTooltip && (
              <div className="feedback-tooltip">
                <div className="tooltip-arrow" />
                {t("contactUsTabTooltip")}
              </div>
            )}
          </div>
        )}

        {/* Contact Us / Feedback Drawer */}
        <ContactUsDrawer 
          isOpen={isFeedbackOpen} 
          onClose={() => setIsFeedbackOpen(false)} 
        />
      </div>
    </LayoutContext.Provider>
  );
}
